import React, { Fragment, useState, useEffect } from 'react'
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Slider
} from '@mui/material'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { Supplier } from '../../../interfaces/Supplier/Supplier'
import { WarehouseMultiselectDLL } from '../../../components/Almacen/UI/WarehouseMultiselectDLL'
import { useWarehouses } from '../../../hooks'
import { ProductOriginsDDL } from '../../../components/Producto/ProductOrigins/ProductOriginsDDL'

interface SupplierFormProps {
  onSubmit: (data: Supplier) => void
  initialData?: Supplier
  isEditing?: boolean
}

const validationSchema = Yup.object().shape({
  supplierName: Yup.string()
    .trim()
    .min(1, 'Nombre del proveedor es requerido')
    .max(100, 'Nombre no puede tener más de 100 caracteres')
    .required('Nombre del proveedor es requerido'),
  rutDniSupplier: Yup.string()
    .trim()
    .min(1, 'RUT/DNI es requerido')
    .max(20, 'RUT/DNI no puede tener más de 20 caracteres')
    .required('RUT/DNI es requerido'),
  minDaysInBound: Yup.number()
    .min(0, 'Los días deben ser mayor o igual a 0')
    .max(365, 'Los días no pueden ser más de 365')
    .required('Tolerancia en días es requerida'),
  // Validaciones para supplierOrigin solo en creación
  supplierOrigin: Yup.array().when('$isEditing', {
    is: false,
    then: Yup.array().of(
      Yup.object().shape({
        supplierOriginName: Yup.string()
          .trim()
          .min(1, 'Nombre del origen es requerido')
          .max(100, 'Nombre del origen no puede tener más de 100 caracteres')
          .required('Nombre del origen es requerido'),
        productControlPercentage: Yup.number()
          .min(0, 'El porcentaje debe ser mayor o igual a 0')
          .max(100, 'El porcentaje debe ser menor o igual a 100')
          .required('Porcentaje de control es requerido'),
        comments: Yup.string().max(
          500,
          'Los comentarios no pueden tener más de 500 caracteres'
        )
      })
    ),
    otherwise: Yup.array()
  })
})

const initialValues: Supplier = {
  id: 0,
  supplierId: 0,
  rutDniSupplier: '',
  supplierName: '',
  minDaysInBound: 0,
  active: true,
  productOriginId: 0,
  warehouses: [],
  supplierOrigin: [
    {
      id: 0,
      supplierId: 0,
      supplierOriginName: '',
      productControlPercentage: 0,
      draft: false,
      comments: '',
      active: true
    }
  ]
}

export const SupplierForm: React.FC<SupplierFormProps> = ({
  onSubmit,
  initialData,
  isEditing = false
}) => {
  const [warehousesIds, setWarehousesIds] = useState<number[]>([])
  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [])

  useEffect(() => {
    // Si es edición, cargar los warehouses existentes
    if (initialData && initialData.warehouses) {
      const warehouseIds = initialData.warehouses.map((w) => w.id)
      setWarehousesIds(warehouseIds)
    }
  }, [initialData])

  const handleSubmitWithValidation = (values: Supplier) => {
    // Validar que se haya seleccionado al menos un warehouse
    if (warehousesIds.length === 0) {
      return // No enviar si no hay warehouses seleccionados
    }

    // Limpiar espacios en blanco
    const cleanedValues = {
      ...values,
      supplierName: values.supplierName.trim(),
      rutDniSupplier: values.rutDniSupplier.trim(),
      supplierOrigin: values.supplierOrigin.map((origin) => ({
        ...origin,
        supplierOriginName: origin.supplierOriginName.trim(),
        comments: origin.comments.trim()
      }))
    }

    // Crear el objeto Supplier con los warehouses correctos
    const supplierData: Supplier = {
      ...cleanedValues,
      warehouses: warehouses?.filter((w) => warehousesIds.includes(w.id)) || []
    }
    onSubmit(supplierData)
  }

  const normalizedInitialValues = {
    ...(initialData || initialValues),
    productOriginId: initialData?.productOriginId || 0
  }

  return (
    <Formik
      initialValues={normalizedInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitWithValidation}
      context={{ isEditing }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        isValid,
        dirty,
        isSubmitting
      }) => {
        // Validar si hay warehouses seleccionados
        const hasWarehousesSelected = warehousesIds.length > 0

        // Detectar si las bodegas cambiaron comparando con los valores iniciales
        const initialWarehouseIds = initialData?.warehouses?.map(w => w.id).sort() || []
        const currentWarehouseIds = [...warehousesIds].sort()
        const warehousesChanged =
          initialWarehouseIds.length !== currentWarehouseIds.length ||
          initialWarehouseIds.some((id, index) => id !== currentWarehouseIds[index])

        const canSubmit =
          isValid && (dirty || warehousesChanged) && hasWarehousesSelected && !isSubmitting

        return (
          <Form>
            <Grid container spacing={2}>
              {/* Campos del Proveedor */}
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name='supplierName'
                  label='Nombre del Proveedor'
                  fullWidth
                  variant='outlined'
                  value={values.supplierName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.supplierName && Boolean(errors.supplierName)}
                  helperText={touched.supplierName && errors.supplierName}
                  disabled={isSubmitting}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name='rutDniSupplier'
                  label='RUT/DNI'
                  fullWidth
                  variant='outlined'
                  value={values.rutDniSupplier}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.rutDniSupplier && Boolean(errors.rutDniSupplier)
                  }
                  helperText={touched.rutDniSupplier && errors.rutDniSupplier}
                  disabled={isSubmitting}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name='minDaysInBound'
                  label='Tolerancia en días de Recepción FEFO'
                  type='number'
                  fullWidth
                  variant='outlined'
                  value={values.minDaysInBound}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.minDaysInBound && Boolean(errors.minDaysInBound)
                  }
                  helperText={touched.minDaysInBound && errors.minDaysInBound}
                  inputProps={{ min: 0 }}
                  disabled={isSubmitting}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name='active'
                      checked={values.active}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  }
                  label='Activo'
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ProductOriginsDDL
                  onChange={(e: any) => {
                    const value = e.target.value as number
                    setFieldValue('productOriginId', value)
                  }}
                  value={values.productOriginId}
                  type='outlined'
                  fullWidth={true}
                />
              </Grid>

              {/* Selección de Warehouses */}
              <Grid item xs={12} sm={6}>
                <WarehouseMultiselectDLL
                  warehousesIds={warehousesIds}
                  setWarehousesIds={setWarehousesIds}
                  fullWidth={true}
                  type='outlined'
                  required={false}
                />
                {!hasWarehousesSelected && (
                  <Typography
                    variant='caption'
                    color='error'
                    style={{ marginTop: 4, display: 'block' }}
                  >
                    Debe seleccionar al menos un almacén
                  </Typography>
                )}
              </Grid>

              {/* Sección de Origen - Solo en Creación */}
              {!isEditing && (
                <Fragment>
                  <Grid item xs={12}>
                    <Divider style={{ margin: '16px 0' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='h6'>Información Origen</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field
                      as={TextField}
                      name='supplierOrigin[0].supplierOriginName'
                      label='Nombre del Origen'
                      fullWidth
                      variant='outlined'
                      value={values.supplierOrigin[0]?.supplierOriginName || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={false}
                      helperText={''}
                      disabled={isSubmitting}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                      <Typography
                        id='productControlPercentage-slider'
                        gutterBottom
                      >
                        Porcentaje de Control:{' '}
                        {values.supplierOrigin[0]?.productControlPercentage ||
                          0}
                        %
                      </Typography>
                      <Slider
                        value={
                          values.supplierOrigin[0]?.productControlPercentage ||
                          0
                        }
                        onChange={(_, newValue) => {
                          const value = Array.isArray(newValue)
                            ? newValue[0]
                            : newValue
                          setFieldValue(
                            'supplierOrigin[0].productControlPercentage',
                            value
                          )
                        }}
                        aria-labelledby='productControlPercentage-slider'
                        step={1}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 25, label: '25%' },
                          { value: 50, label: '50%' },
                          { value: 75, label: '75%' },
                          { value: 100, label: '100%' }
                        ]}
                        min={0}
                        max={100}
                        valueLabelDisplay='auto'
                        disabled={isSubmitting}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant='outlined'>
                      <InputLabel>Requiere Draft</InputLabel>
                      <Select
                        value={values.supplierOrigin[0]?.draft || false}
                        onChange={(e) =>
                          setFieldValue(
                            'supplierOrigin[0].draft',
                            e.target.value === 'true'
                          )
                        }
                        label='Requiere Draft'
                        disabled={isSubmitting}
                      >
                        <MenuItem value='true'>Sí</MenuItem>
                        <MenuItem value='false'>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name='supplierOrigin[0].comments'
                      label='Observaciones'
                      fullWidth
                      variant='outlined'
                      multiline
                      rows={3}
                      value={values.supplierOrigin[0]?.comments || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={false}
                      helperText={''}
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Fragment>
              )}

              <Grid item xs={12}>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  fullWidth
                  size='large'
                  disabled={!canSubmit}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : undefined
                  }
                >
                  {isSubmitting
                    ? 'Procesando...'
                    : `${isEditing ? 'Actualizar' : 'Crear'} Proveedor`}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}
